import React, { useCallback, useEffect } from "react";
import { useParams } from "@reach/router";
import { navigate } from "gatsby";
import { Text, InlineLoader } from "@ifgengineering/component-library";
import { StringParam, useQueryParam } from "use-query-params";
import EisFundStartupDetail from "./EisFundStartupDetail";
import { ErrorContainer, ErrorBackButton } from "./styled";
import { useEisFundInvestments } from "./useEisFundInvestments";
import { logException } from "@helpers/logException/logException";

interface EisFundStartupDetailContainerProps {
  path?: string;
}

const EisFundStartupDetailContainer: React.FC<
  EisFundStartupDetailContainerProps
> = () => {
  const { dealId, startupId } = useParams();
  const [commitmentIdParam] = useQueryParam("commitmentId", StringParam);

  const {
    investments,
    isLoading,
    error,
    refetch,
  } = useEisFundInvestments({
    dealId: dealId ?? "",
    commitmentId: commitmentIdParam ?? "",
    skip: !dealId || !commitmentIdParam,
  });

  const startup = investments.find((inv) => inv.id === startupId);

  const handleBack = useCallback(() => {
    navigate(
      `/app/deals/${dealId}/eis-fund?commitmentId=${commitmentIdParam}`
    );
  }, [dealId, commitmentIdParam]);

  useEffect(() => {
    if (!isLoading && !error && !startup && startupId && investments.length > 0) {
      logException({
        tag: "EisFundStartupDetailContainer",
        message: `Startup not found: ${startupId}`,
        exception: new Error(`Startup with ID ${startupId} not found in investments`),
      });
    }
  }, [isLoading, error, startup, startupId, investments.length]);

  if (!dealId || !startupId) {
    return (
      <ErrorContainer data-testid="missing-params-error">
        <Text type="H24" color="SLATE800">
          Missing required parameters
        </Text>
        <Text type="P16" color="SLATE600">
          {!dealId
            ? "A deal ID is required to view startup details."
            : "A startup ID is required to view startup details."}
        </Text>
        <ErrorBackButton onClick={() => navigate(-1)}>Go Back</ErrorBackButton>
      </ErrorContainer>
    );
  }

  if (isLoading) {
    return (
      <ErrorContainer data-testid="loading-state">
        <InlineLoader />
        <Text type="P16" color="SLATE600">
          Loading startup details...
        </Text>
      </ErrorContainer>
    );
  }

  if (error) {
    return (
      <ErrorContainer data-testid="error-state">
        <Text type="H24" color="ERROR800">
          Error loading startup details
        </Text>
        <Text type="P16" color="SLATE600">
          {error || "Failed to load startup details. Please try again."}
        </Text>
        <ErrorBackButton onClick={() => refetch()}>Retry</ErrorBackButton>
      </ErrorContainer>
    );
  }

  if (!startup) {
    return (
      <ErrorContainer data-testid="not-found-state">
        <Text type="H24" color="SLATE800">
          Startup not found
        </Text>
        <Text type="P16" color="SLATE600">
          The startup you're looking for could not be found.
        </Text>
        <ErrorBackButton onClick={handleBack}>
          Back to EIS Fund
        </ErrorBackButton>
      </ErrorContainer>
    );
  }

  return <EisFundStartupDetail startup={startup} onBack={handleBack} />;
};

export default EisFundStartupDetailContainer;
